import type { ReactNode } from "react";
import { AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { HelpCircle } from "lucide-react";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { MotionSlideLeft } from "@themes/pollinator/ui/MotionSlideLeft";
import { Button } from "@themes/pollinator/ui/Button";
import { SupportDialogs } from "@platform/features/support";

type Props = {
  title?: string;
  videoGrid: ReactNode;
  controls: ReactNode;
  sidebar?: ReactNode;
  subtitles?: ReactNode;
  introDialog?: ReactNode;
  isSidebarOpen: boolean;
  isSupportDialogEnabled?: boolean;
  onSupportClick?: () => void;
};

export function VideoMeetingFrame({
  title,
  videoGrid,
  controls,
  sidebar,
  subtitles,
  introDialog,
  isSidebarOpen,
  isSupportDialogEnabled = false,
  onSupportClick,
}: Props) {
  return (
    <>
      <Helmet>
        <title>
          {["Pollinator", "Meeting", title].filter(Boolean).join(" | ")}
        </title>
      </Helmet>
      <div className="bg-brand-900 h-[100dvh] w-full flex flex-row overflow-hidden min-w-xs">
        <div className="relative h-full flex flex-1 flex-col justify-between min-w-0">
          <div className="flex justify-center px-4 pt-4 pb-2 md:pb-4 md:pt-6">
            <PollinatorLogo size="sm" theme="light" />
            {isSupportDialogEnabled && onSupportClick && (
              <Button
                variant="ghost"
                theme="dark"
                className="absolute right-2 top-2 md:right-4 md:top-6"
                iconComponent={HelpCircle}
                onClick={onSupportClick}
              />
            )}
          </div>
          <div className="relative flex flex-col flex-1 items-center justify-center w-full h-full overflow-hidden px-4 pt-0">
            {videoGrid}
            {introDialog}
          </div>
          {subtitles}
          {controls}
        </div>
        <AnimatePresence mode="wait">
          {isSidebarOpen && sidebar && (
            <MotionSlideLeft key="video-meeting-sidebar" className="h-full">
              {sidebar}
            </MotionSlideLeft>
          )}
        </AnimatePresence>
      </div>
      {isSupportDialogEnabled && <SupportDialogs />}
    </>
  );
}
